import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageLayout from "../../../common/layout/PageLayout";
import {
    BadgeCounter,
    BreadCrumb,
    Checkbox,
    CustomAnchor,
    FloatingPanel,
    FileViewer,
    InfiniteScrollList,
    ListItem,
    MasterFilter,
    MultipleFileUploadModal,
    PageProgress,
    PopoverMenuButton,
    SortMenu,
} from "../../../components";

import AffiliateFiltersWizard, { getAffiliateActionTitle } from "./affiliate-filters-wizard";
import ManageAnnexesWizard, { getAnnexActionTitle } from "./manage-annexes-wizard";
import SelectAccFilesModal from "./select-acc-files-modal";
import EditDocDetailsModal from "../edit-doc-details-modal";
import DeleteDocsModal from "../delete-docs-modal";
import useDocsSelection from "./use-docs-selection";
import { useAccDocsImport, useDeviceType, useDocDownload, useDocs, useDocsCsvImport, useFakeProgress } from "../../../hooks";
import { exportDocsAPI, importDocsCsvAPI, uploadDocsAPI } from "../../../common/services";
import { useToaster } from "../../../common/contexts/toast";
import { getMetadataItems, isAnnexDoc, DOC_SORT_OPTIONS, getDocExtension, hasNonDefaultDocFilters, applyFilesSelectionReset, flattenDocsWithChildren } from "../utils";
import { errorMessageOf } from "../../../utils";
import { DOC_FILES_FIELD_ID, DOC_FILES_OPTION_IDS, DOC_UPLOAD_FILE_TYPES, buildDefaultDocsFilterValue, getDocFilesOptions, getDocsMatchBarStaticFields, getBackendDocSortValue } from "../../../constants/docs";

const ManageDocs = () => {
    const [showLocalUploadModal, setShowLocalUploadModal] = useState(false);
    const [showImportListModal, setShowImportListModal] = useState(false);
    const [showAccUploadModal, setShowAccUploadModal] = useState(false);
    const [affiliateMode, setAffiliateMode] = useState(false);
    const [affiliateAction, setAffiliateAction] = useState(null); // null while the user is still on step one
    const [annexMode, setAnnexMode] = useState(false);
    const [annexAction, setAnnexAction] = useState(null); // null while the user is still on step one
    const [previewDoc, setPreviewDoc] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [docToEdit, setDocToEdit] = useState(null);

    const history = useHistory();
    const { isMobile } = useDeviceType();

    const { showToaster } = useToaster();
    const { downloadDoc } = useDocDownload();
    const goToDocsView = useCallback(() => history.push("/docs"), [history]);
    const exitWizards = useCallback(() => {
        setAffiliateMode(false);
        setAffiliateAction(null);
        setAnnexMode(false);
        setAnnexAction(null);
    }, []);

    // Search state transferred from the docs page ("Edit list"); absent on
    // refresh / direct links.
    const location = useLocation();
    const transferredSearchState = location.state?.docsSearchState;

    const {
        docsList: docs,
        totalDocs,
        totalDocsWithAnnexes,
        isLoading,
        isScrollLoader,
        filterValue,
        searchFilters,
        filterOptions,
        filterFields,
        visibleParams,
        sortValue,
        onChangeFilter,
        onChangeSort,
        handleScroll,
        refreshDocs,
    } = useDocs({
        defaultFilterValue: transferredSearchState?.filterValue || defaultManageFilterValue,
        defaultSortValue: transferredSearchState?.sortValue,
    });

    // The exported CSV, corrected and sent back. The rows are applied in the
    // background, so the list reloads once the job lands.
    const { isImporting, trackImport } = useDocsCsvImport({
        onFinished: ({ success, message }) => {
            showToaster && showToaster("docs-import", message, success);
            if (success) refreshDocs();
        },
    });

    // ACC files and folders are copied by a background job too; same loader.
    const { isImporting: isAccImporting, trackImport: trackAccImport, progressText: accImportText } = useAccDocsImport({
        onFinished: ({ success, message }) => {
            showToaster && showToaster("acc-import", message, success);
            refreshDocs();
        },
    });

    const importProgress = useFakeProgress(isImporting || isAccImporting);

    const handleMatchBarFilterChange = useCallback((newFilterValue) => {
        onChangeFilter(applyFilesSelectionReset(filterValue, newFilterValue));
    }, [filterValue, onChangeFilter]);

    const allMatchBarFields = useMemo(() => [...matchBarStaticFields, ...filterFields], [filterFields]);

    // Facet options plus the static Files chips options
    const allFilterOptions = useMemo(
        () => ({ ...filterOptions, [DOC_FILES_FIELD_ID]: docFilesOptions }),
        [filterOptions]
    );

    const allVisibleParams = useMemo(() => {
        const staticParams = matchBarStaticFields.reduce((obj, fieldData) => ({ ...obj, [fieldData.id]: true }), {});
        return { ...staticParams, ...visibleParams };
    }, [visibleParams]);

    // Each parent carries its annexes inline as `children`; flatten so both
    // parents and their annexes are listed and individually selectable.
    const flatDocs = useMemo(() => flattenDocsWithChildren(docs), [docs]);

    const {
        isSelectAll,
        isAllSelected,
        count: selectedCount,
        parentCount: selectedParentCount,
        selectedDocs,
        isSelected,
        isLocked,
        toggleRow,
        toggleAll,
        clear: clearSelection,
        buildRequest,
    } = useDocsSelection({ docs: flatDocs, totalDocs, totalDocsWithAnnexes, searchFilters });

    // Affiliate filters apply to non-annex files; managing annexes works on any selection.
    const affiliateDocs = useMemo(() => selectedDocs.filter((d) => !isAnnexDoc(d)), [selectedDocs]);
    const affiliateSelection = useMemo(() => buildRequest(affiliateDocs), [buildRequest, affiliateDocs]);
    const affiliateCount = isSelectAll ? selectedParentCount : affiliateDocs.length;
    const selectedDocIds = useMemo(() => new Set(selectedDocs.map((d) => d.id)), [selectedDocs]);

    // After a wizard applies its changes: clear the selection, refetch the list
    // (children/tags may have moved), and return to the docs view.
    const onWizardDone = useCallback(() => {
        clearSelection();
        refreshDocs();
        exitWizards();
    }, [clearSelection, refreshDocs, exitWizards]);

    const onSelectionOutdated = useCallback(() => {
        clearSelection();
        refreshDocs();
        exitWizards();
    }, [clearSelection, refreshDocs, exitWizards]);

    // The open wizard adds its own crumb, plus its action once one is chosen.
    const breadCrumbs = useMemo(() => {
        if (affiliateMode) {
            const actionTitle = getAffiliateActionTitle(affiliateAction);
            return actionTitle
                ? [...AFFILIATE_BREADCRUMBS, { id: "affiliate-action", displayName: actionTitle }]
                : AFFILIATE_BREADCRUMBS;
        }
        if (annexMode) {
            const actionTitle = getAnnexActionTitle(annexAction);
            return actionTitle
                ? [...ANNEX_BREADCRUMBS, { id: "annex-action", displayName: actionTitle }]
                : ANNEX_BREADCRUMBS;
        }
        return DOCS_BREADCRUMBS;
    }, [affiliateMode, annexMode, affiliateAction, annexAction]);

    const onBreadCrumbClick = useCallback((newCrumbs) => {
        const target = newCrumbs[newCrumbs.length - 1]?.id;
        if (target === "docs-view") goToDocsView();
        else if (target === "edit-view") exitWizards();
        else if (target === "manage-fa") setAffiliateAction(null);
        else if (target === "manage-annex") setAnnexAction(null);
    }, [goToDocsView, exitWizards]);

    const openDocViewer = useCallback((doc) => {
        setPreviewDoc(doc);
    }, []);

    const closeDocViewer = useCallback(() => {
        setPreviewDoc(null);
    }, []);

    // A deleted row (and any annex deleted with it) leaves the selection.
    const onDocsDeleted = useCallback(() => {
        clearSelection();
        refreshDocs();
    }, [clearSelection, refreshDocs]);

    const getRowMenuItems = useCallback((doc) => [
        { id: `doc-download-${doc.id}`, label: "Download",     src: "/images/icon/download_grey.svg", className: "download-btn", onClick: () => downloadDoc(doc) },
        { id: `doc-edit-${doc.id}`,     label: "Edit details", src: "/images/icon/pencil.svg",        onClick: () => setDocToEdit(doc) },
        { id: `doc-delete-${doc.id}`,   label: "Delete",       src: "/images/icon/trash.svg",         onClick: () => setPendingDelete({ selection: { docIds: [doc.id] }, count: 1 }) },
    ], [downloadDoc]);

    // Export the on-screen list as a ZIP (CSV + guidelines), same filters as the search.
    const handleDownloadCsv = useCallback(async () => {
        try {
            const { tags, ...filters } = searchFilters || {};
            const ok = await exportDocsAPI({
                ...filters,
                ...(tags ? { tags: JSON.stringify(tags) } : {}),
                sort: getBackendDocSortValue(sortValue),
            });
            if (!ok) showToaster && showToaster("docs-csv-error", "Couldn't download the list.", false);            
        } catch (error) {
            console.log("Error: ", error);
            showToaster && showToaster("docs-csv-error", errorMessageOf(error, "Couldn't download the list."), false)
        }
    }, [searchFilters, sortValue, showToaster]);

    const manageFilesOptions = useMemo(() => {
        const hasSelection = selectedCount > 0;
        const hasNonAnnex = affiliateCount > 0;
        return [
            { id: "download-csv",      label: "Export docs list",                 src: "/images/icon/csv.svg",                                                    onClick: handleDownloadCsv },
            { id: "upload-list",       label: "Upload updated list",              src: "/images/icon/upload-file.svg",     disabled: isImporting,  withHr: true,  onClick: () => setShowImportListModal(true) },
            { id: "upload-acc",        label: "Upload from ACC",                  src: "/images/icon/autodesk-black.svg",                                         onClick: () => setShowAccUploadModal(true) },
            { id: "upload-local",      label: "Upload local files",               src: "/images/icon/upload-file.svg",     withHr: true,                          onClick: () => setShowLocalUploadModal(true) },
            { id: "affiliate-filters", label: "Affiliate filters and attributes", src: "/images/icon/matchbar.svg",        disabled: !hasNonAnnex,  withHr: true, onClick: () => { setAffiliateAction(null); setAffiliateMode(true); } },
            { id: "manage-annexes",    label: "Manage annexes",                   src: "/images/icon/list-item-child.svg", disabled: !hasSelection || isSelectAll, withHr: true, onClick: () => { setAnnexAction(null); setAnnexMode(true); } },
            { id: "delete",            label: "Delete selected files",            src: "/images/icon/trash.svg",           disabled: !hasSelection,               onClick: () => setPendingDelete({ selection: buildRequest(), count: selectedCount }) },
        ];
    }, [selectedCount, affiliateCount, isSelectAll, buildRequest, handleDownloadCsv, isImporting]);

    return (
        <>
            <PageLayout className={`manage-docs-page-layout ${isMobile ? "mobile" : ""} ${(isImporting || isAccImporting) ? "fill-height" : ""}`}>
                {isImporting ? (
                <PageProgress progress={importProgress} text="Updating your docs list... this might take a while" />
                ) : isAccImporting ? (
                <PageProgress progress={importProgress} text={accImportText} />
                ) : (
                <>
                <div className="manage-docs-breadcrumb-bar-container">
                    <div className="container manage-docs-breadcrumb-bar">
                        <BreadCrumb
                            breadCrumbs={breadCrumbs}
                            onGoBack={goToDocsView}
                            onClick={onBreadCrumbClick}
                        />
                    </div>
                </div>
                {affiliateMode ? (
                    <div className="container docs-wizard-wrapper">
                        <AffiliateFiltersWizard
                            docs={affiliateDocs}
                            selection={affiliateSelection}
                            count={affiliateCount}
                            action={affiliateAction}
                            onChangeAction={setAffiliateAction}
                            onManageApplied={refreshDocs}
                            onApply={onWizardDone}
                            onSelectionOutdated={onSelectionOutdated}
                        />
                    </div>
                ) : annexMode ? (
                    <div className="container docs-wizard-wrapper">
                        <ManageAnnexesWizard
                            docs={selectedDocs}
                            allDocs={flatDocs}
                            selectedIds={selectedDocIds}
                            action={annexAction}
                            onChangeAction={setAnnexAction}
                            onApply={onWizardDone}
                        />
                    </div>
                ) : (
                <>
                {isMobile && (
                    <div className="manage-docs-matchbar-wrapper container">
                        <MasterFilter
                            value={filterValue}
                            onChange={handleMatchBarFilterChange}
                            options={allFilterOptions}
                            otherMatchBarFields={allMatchBarFields}
                            fields={matchBarStaticFields}
                            tagSelectorVisibleParameters={visibleParams}
                            visibileParameters={allVisibleParams}
                            withRightPanelFilters
                            masterSearchWithFilterBox={false}
                            disabled={allMatchBarFields.length === 0}
                            showClearAll={hasNonDefaultDocFilters(filterValue, defaultManageFilterValue)}
                            showClearAllOnEmpty
                            clearBtnText="Reset"
                            onClearAll={() => onChangeFilter(defaultManageFilterValue)}
                        />
                    </div>
                )}
                <div className="manage-docs-page container">
                    {!isMobile && (
                        <aside className="manage-docs-sidebar">
                            <MasterFilter
                                value={filterValue}
                                onChange={handleMatchBarFilterChange}
                                options={allFilterOptions}
                                otherMatchBarFields={allMatchBarFields}
                                fields={matchBarStaticFields}
                                tagSelectorVisibleParameters={visibleParams}
                                withTagSelector
                                withMasterSearch={false}
                                withSearch
                                disabled={allMatchBarFields.length === 0}
                            />
                        </aside>
                    )}

                    <section className="manage-docs-main">
                        <header className="manage-docs-toolbar">
                            <div className="manage-docs-toolbar-left">
                                <Checkbox
                                    id="manage-docs-select-all"
                                    label="Select all"
                                    value={isAllSelected}
                                    onChange={toggleAll}
                                />
                                <BadgeCounter count={`${selectedCount} /${totalDocsWithAnnexes}`} imgSrc="/images/icon/spec.svg" />
                            </div>
                            <div className="manage-docs-toolbar-right">
                                <SortMenu options={DOC_SORT_OPTIONS} value={sortValue} onChange={onChangeSort} />
                                <PopoverMenuButton label="MANAGE FILES" options={manageFilesOptions} />
                            </div>
                        </header>

                        <InfiniteScrollList
                            className="manage-docs-rows"
                            isLoading={isLoading}
                            isLoadingMore={isScrollLoader}
                            onScroll={handleScroll}
                            emptyText="No documents found"
                        >
                            {docs.map((doc) => (
                                <React.Fragment key={doc.id}>
                                    <DocRow
                                        doc={doc}
                                        selected={isSelected(doc)}
                                        locked={isLocked(doc)}
                                        onToggle={toggleRow}
                                        onOpen={openDocViewer}
                                        showItemsInTooltip={isMobile}
                                        getRowMenuItems={getRowMenuItems}
                                    />
                                    {(doc.children || []).map((child) => (
                                        <DocRow
                                            key={child.id}
                                            doc={child}
                                            selected={isSelected(child)}
                                            locked={isLocked(child)}
                                            onToggle={toggleRow}
                                            onOpen={openDocViewer}
                                            showItemsInTooltip={isMobile}
                                            getRowMenuItems={getRowMenuItems}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                        </InfiniteScrollList>
                    </section>
                </div>
                </>
                )}
                </>
                )}
            </PageLayout>

            <DeleteDocsModal
                visible={!!pendingDelete}
                selection={pendingDelete?.selection}
                count={pendingDelete?.count}
                onClose={() => setPendingDelete(null)}
                onDeleted={onDocsDeleted}
                onSelectionOutdated={onSelectionOutdated}
            />

            <EditDocDetailsModal
                visible={!!docToEdit}
                doc={docToEdit}
                onClose={() => setDocToEdit(null)}
                onUpdated={refreshDocs}
            />

            <MultipleFileUploadModal
                visible={showLocalUploadModal}
                onClose={() => setShowLocalUploadModal(false)}
                onUploaded={refreshDocs}
                title="Select documents"
                description="Upload the project's documents"
                note="You can select up to 10 files; all must be digital-text files (PDF, Word, Excel, CSV or TSV - not scanned images of text)."
                validations={{ supportedFileTypes: DOC_UPLOAD_FILE_TYPES, maxUploadFileQuantity: 10 }}
                uploadRequest={uploadDocsAPI}
            />

            <MultipleFileUploadModal
                visible={showImportListModal}
                onClose={() => setShowImportListModal(false)}
                onUploaded={(data) => trackImport(data?.job)}
                title="Upload an updated docs list"
                description="Upload the CSV of your docs list"
                note="You can only use the CSV file which was downloaded from the Bimmatch platform."
                confirmButtonName="UPLOAD"
                validations={{ supportedFileTypes: ["csv"], maxUploadFileQuantity: 1 }}
                uploadRequest={importDocsCsvAPI}
                fileFieldName="file"
                successMessage={() => "Upload received. Updating your docs list…"}
                errorFallback="Couldn't upload the docs list."
                modalClass="docs-import-modal"
            />

            <SelectAccFilesModal
                visible={showAccUploadModal}
                onClose={() => setShowAccUploadModal(false)}
                onImportQueued={trackAccImport}
                importing={isAccImporting}
            />

            <FloatingPanel
                id="manage-doc-viewer"
                direction="right"
                open={!!previewDoc}
                title={previewDoc?.displayName || "Document"}
                onClose={closeDocViewer}
                size="medium"
                className="manage-docs-floating-panel"
            >
                {previewDoc && (
                    <FileViewer
                        file={previewDoc.file}
                        docId={previewDoc.id}
                        fileType={previewDoc.fileType}
                        onError={(err) => console.log("[manage-docs] file viewer error", err)}
                    />
                )}
            </FloatingPanel>
        </>
    );
};

const DocRow = ({ doc, selected, locked, onToggle, onOpen, showItemsInTooltip, getRowMenuItems }) => {
    const ext = getDocExtension(doc);

    const handleCheckboxChange = useCallback(() => {
        onToggle(doc);
    }, [doc, onToggle]);

    const handleNameClick = useCallback(() => {
        onOpen(doc);
    }, [doc, onOpen]);

    const items = useMemo(() => getMetadataItems(doc), [doc]);
    const isAnnex = isAnnexDoc(doc);

    return (
        <div className={`manage-docs-row ${selected ? "selected" : ""} ${isAnnex ? "is-annex" : ""} ${locked ? "selection-locked" : ""}`}>
            <div
                className="manage-docs-row-checkbox"
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox
                    id={`manage-docs-row-${doc.id}`}
                    hideLabel
                    value={selected}
                    onChange={handleCheckboxChange}
                />
            </div>
            {isAnnex && (
                <img
                    src="/images/icon/list-item-child.svg"
                    alt="annex"
                    className="manage-docs-annex-icon"
                />
            )}
            <ListItem
                title={<DocTitle title={doc?.displayName} documentNo={doc?.documentNo} onDocClick={handleNameClick} />}
                titleTooltip={doc?.displayName}
                type="secondary"
                items={items}
                showItemsInTooltip={showItemsInTooltip}
                popoverClassName="doc-item-popover"
                renderRightSection={ext ? <span className="doc-file-ext">{ext}</span> : null}
                optionMenuItems={getRowMenuItems ? getRowMenuItems(doc) : undefined}
            />
        </div>
    );
};

const docFilesOptions = getDocFilesOptions();
const matchBarStaticFields = getDocsMatchBarStaticFields(docFilesOptions);
const defaultManageFilterValue = buildDefaultDocsFilterValue([DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS]);

const DOCS_BREADCRUMBS = [
    { id: "docs-view", displayName: "Docs view" },
    { id: "edit-view", displayName: "Edit view" },
];

const AFFILIATE_BREADCRUMBS = [
    ...DOCS_BREADCRUMBS,
    { id: "manage-fa", displayName: "Manage filters and attributes" },
];

const ANNEX_BREADCRUMBS = [
    ...DOCS_BREADCRUMBS,
    { id: "manage-annex", displayName: "Manage annex files" },
];

const DocTitle = ({ title, documentNo, onDocClick }) => (
    <div className="doc-title">
        <CustomAnchor onClick={onDocClick}>{documentNo}</CustomAnchor>
        <CustomAnchor onClick={onDocClick} className="doc-title-text">{title}</CustomAnchor>
    </div>
);

export default ManageDocs;
